import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { createUser, updateUser, getAllUsers } from '../services/users';
import { getEmailTemplates, saveEmailTemplate, getDefaultTemplates } from '../services/templates';
import { getOrderFormTemplate, saveOrderFormTemplate, getDefaultOrderFormTemplate } from '../services/orderFormTemplates';
import { getLetterTemplates, saveLetterTemplate, getDefaultLetterTemplates } from '../services/letterTemplates';
import { getClientSources, createClientSource, updateClientSource, deleteClientSource } from '../services/sources';
import { getTaskTypes, createTaskType, updateTaskType, deleteTaskType } from '../services/taskTypes';
import { getAuditLogFiltered } from '../services/audit';
import { getDefaultTaxRate, saveDefaultTaxRate, getEnableDueDates, saveEnableDueDates } from '../services/settings';
import { getAllClockInLog } from '../services/clockInOut';
import { supabase } from '../lib/supabase';
import { isAdminOrManager } from '../utils/roleHelpers';
import ReactQuill from 'react-quill';
import '../utils/quillVariableBlot'; // Import custom variable blot (legacy)
import '../editor/mergeTokenBlot'; // Import merge token blot (new Embed-based system)
import TemplateActionBar from '../components/template/TemplateActionBar';
import TemplateEditorLayout from '../components/template/TemplateEditorLayout';
import { assembleDocument } from '../utils/assembleDocument';
import './Settings.css';

export default function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'tech',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Template sub-tab state
  const [templateSubTab, setTemplateSubTab] = useState('email'); // 'email', 'letter', or 'order'
  
  // Email templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({
    template_type: '',
    subject: '',
    body: '',
  });
  const [templateError, setTemplateError] = useState('');
  const [templateSuccess, setTemplateSuccess] = useState('');


  // Order form template state
  const [orderFormTemplate, setOrderFormTemplate] = useState(null);
  const [loadingOrderFormTemplate, setLoadingOrderFormTemplate] = useState(true);
  const [editingOrderFormTemplate, setEditingOrderFormTemplate] = useState(false);
  const [orderFormTemplateFormData, setOrderFormTemplateFormData] = useState({
    subject: '',
    body: '',
  });
  const [orderFormTemplateError, setOrderFormTemplateError] = useState('');
  const [orderFormTemplateSuccess, setOrderFormTemplateSuccess] = useState('');

  // Letter templates state
  const [letterTemplates, setLetterTemplates] = useState([]);
  const [loadingLetterTemplates, setLoadingLetterTemplates] = useState(true);
  const [editingLetterTemplate, setEditingLetterTemplate] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [letterTemplateFormData, setLetterTemplateFormData] = useState({
    template_type: '',
    header: '', // Header section
    body: '', // Body section
    footer: '', // Footer section
    document: '', // Legacy: Combined document (for backward compatibility)
  });
  const [letterTemplateError, setLetterTemplateError] = useState('');
  const [letterTemplateSuccess, setLetterTemplateSuccess] = useState('');

  // Client sources state
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [showAddSource, setShowAddSource] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceFormData, setSourceFormData] = useState({ name: '' });
  const [sourceError, setSourceError] = useState('');
  const [sourceSuccess, setSourceSuccess] = useState('');

  // Task types state
  const [taskTypes, setTaskTypes] = useState([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(true);
  const [showAddTaskType, setShowAddTaskType] = useState(false);
  const [editingTaskType, setEditingTaskType] = useState(null);
  const [taskTypeFormData, setTaskTypeFormData] = useState({
    name: '',
    code: '',
    description: '',
    default_status: 'scheduled',
    display_order: 0,
    active: true,
    requires_completion_modal: false,
    allow_completion_note: true,
    allow_create_follow_up: false,
    completion_note_label: 'Completion Notes',
    completion_note_placeholder: 'Add any notes about task completion...',
    follow_up_label: 'Create Follow-Up Task',
    follow_up_hint: 'Check this to automatically create a follow-up task after completing this one.',
    require_arrival_time: false,
    require_start_time: false,
    require_completion_note: false,
    allow_reschedule_needed: false,
    require_note_when_reschedule: false,
    requires_measurements: false,
    link_to_invoice: false,
    hide_edit_button: false,
    hide_completion_button: false,
    hide_delete_button: false,
    hide_complete_button: false,
  });
  const [taskTypeError, setTaskTypeError] = useState('');
  const [taskTypeSuccess, setTaskTypeSuccess] = useState('');
  const [editingCompletionModalTaskType, setEditingCompletionModalTaskType] = useState(null);
  const [completionModalFormData, setCompletionModalFormData] = useState({
    requires_completion_modal: false,
    allow_completion_note: true,
    allow_create_follow_up: false,
    completion_note_label: 'Completion Notes',
    completion_note_placeholder: 'Add any notes about task completion...',
    follow_up_label: 'Create Follow-Up Task',
    follow_up_hint: 'Check this to automatically create a follow-up task after completing this one.',
    require_arrival_time: false,
    require_start_time: false,
    require_completion_note: false,
    allow_reschedule_needed: false,
    require_note_when_reschedule: false,
    requires_measurements: false,
    link_to_invoice: false,
    hide_edit_button: false,
    hide_completion_button: false,
    hide_delete_button: false,
    hide_complete_button: false,
  });
  const [completionModalSaveError, setCompletionModalSaveError] = useState('');
  const [completionModalSaveSuccess, setCompletionModalSaveSuccess] = useState('');

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(true);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const AUDIT_PAGE_SIZE = 25;
  const [auditFilters, setAuditFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    entityType: '',
    action: '',
  });

  // Tax rate / application settings state
  const [defaultTaxRate, setDefaultTaxRate] = useState(11);
  const [savedTaxRate, setSavedTaxRate] = useState(11);
  const [enableDueDates, setEnableDueDates] = useState(true);
  const [savedEnableDueDates, setSavedEnableDueDates] = useState(true);
  const [loadingTaxRate, setLoadingTaxRate] = useState(true);
  const [savingTaxRate, setSavingTaxRate] = useState(false);
  const [savingDueDates, setSavingDueDates] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState({ type: '', text: '' });

  // Text size (localStorage, 0.8–1.4)
  const [textSize, setTextSize] = useState(() => {
    try {
      const v = localStorage.getItem('appTextSize');
      const n = parseFloat(v);
      return (!Number.isNaN(n) && n >= 0.8 && n <= 1.4) ? n : 1;
    } catch { return 1; }
  });

  // Apply text size on mount
  useEffect(() => {
    try {
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--app-text-size', String(textSize));
      }
    } catch (_) {}
  }, [textSize]);

  // Clock in/out log state
  const [clockInLog, setClockInLog] = useState([]);
  const [loadingClockInLog, setLoadingClockInLog] = useState(true);
  const [clockInLogEmployeeFilter, setClockInLogEmployeeFilter] = useState('');
  const [clockInLogWeekFilter, setClockInLogWeekFilter] = useState('all');

  // Load users when component mounts or when users tab is active
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback to current user
      if (user) {
        setUsers([user]);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'clock-in-log') {
      loadUsers();
    }
  }, [activeTab, loadUsers]);

  useEffect(() => {
    if (user && users.length === 0) {
      setUsers([user]);
    }
  }, [user, users.length]);

  const loadOrderFormTemplate = useCallback(async () => {
    try {
      setLoadingOrderFormTemplate(true);
      const data = await getOrderFormTemplate();
      setOrderFormTemplate(data);
      setOrderFormTemplateFormData({
        subject: data.subject || '',
        body: data.body || '',
      });
    } catch (error) {
      console.error('Error loading order form template:', error);
      const defaultTemplate = getDefaultOrderFormTemplate();
      setOrderFormTemplate(defaultTemplate);
      setOrderFormTemplateFormData({
        subject: defaultTemplate.subject || '',
        body: defaultTemplate.body || '',
      });
    } finally {
      setLoadingOrderFormTemplate(false);
    }
  }, []);

  const loadDefaultTaxRate = useCallback(async () => {
    try {
      setLoadingTaxRate(true);
      const rate = await getDefaultTaxRate();
      setDefaultTaxRate(rate);
      setSavedTaxRate(rate);
    } catch (error) {
      console.error('Error loading default tax rate:', error);
      setDefaultTaxRate(11);
      setSavedTaxRate(11);
    } finally {
      setLoadingTaxRate(false);
    }
  }, []);

  const loadEnableDueDates = useCallback(async () => {
    try {
      const enabled = await getEnableDueDates();
      setEnableDueDates(enabled);
      setSavedEnableDueDates(enabled);
    } catch (error) {
      console.error('Error loading enable due dates setting:', error);
      setEnableDueDates(true);
      setSavedEnableDueDates(true);
    }
  }, []);

  const showSettingsMessage = (type, text) => {
    setSettingsMessage({ type, text });
    setTimeout(() => setSettingsMessage({ type: '', text: '' }), 4000);
  };

  const handleSaveTaxRate = async (e) => {
    e.preventDefault();
    setSettingsMessage({ type: '', text: '' });
    const num = typeof defaultTaxRate === 'number' ? defaultTaxRate : parseFloat(defaultTaxRate, 10);
    if (Number.isNaN(num)) {
      showSettingsMessage('error', 'Please enter a valid number.');
      return;
    }
    if (num < 0 || num > 100) {
      showSettingsMessage('error', 'Tax rate must be between 0 and 100.');
      return;
    }
    try {
      setSavingTaxRate(true);
      await saveDefaultTaxRate(num);
      setSavedTaxRate(num);
      setDefaultTaxRate(num);
      showSettingsMessage('success', 'Saved');
    } catch (error) {
      showSettingsMessage('error', error.message || 'Failed to save tax rate.');
    } finally {
      setSavingTaxRate(false);
    }
  };

  const handleSaveEnableDueDates = async (e) => {
    e.preventDefault();
    setSettingsMessage({ type: '', text: '' });
    try {
      setSavingDueDates(true);
      await saveEnableDueDates(enableDueDates);
      setSavedEnableDueDates(enableDueDates);
      showSettingsMessage('success', 'Saved');
    } catch (error) {
      showSettingsMessage('error', error.message || 'Failed to save due date setting.');
    } finally {
      setSavingDueDates(false);
    }
  };

  const taxRateNum = typeof defaultTaxRate === 'number' ? defaultTaxRate : parseFloat(defaultTaxRate, 10);
  const taxRateValid = defaultTaxRate !== '' && !Number.isNaN(taxRateNum) && taxRateNum >= 0 && taxRateNum <= 100;
  const taxRateDirty = taxRateValid && savedTaxRate !== taxRateNum;
  const dueDatesDirty = savedEnableDueDates !== enableDueDates;

  // Get week range for clock log filter (Monday-based). Returns { dateFrom, dateTo } as ISO or nulls for 'all'.
  const getClockLogWeekRange = useCallback((option) => {
    if (!option || option === 'all') return { dateFrom: null, dateTo: null };
    const now = new Date();
    const getMonday = (d) => {
      const date = new Date(d);
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      date.setDate(diff);
      date.setHours(0, 0, 0, 0);
      return date;
    };
    const weeksAgo = option === 'current' ? 0 : (option === 'last' ? 1 : parseInt(option, 10) || 1);
    const weekStart = getMonday(now);
    weekStart.setDate(weekStart.getDate() - 7 * weeksAgo);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    return { dateFrom: weekStart.toISOString(), dateTo: weekEnd.toISOString() };
  }, []);

  const loadClockInLog = useCallback(async () => {
    try {
      setLoadingClockInLog(true);
      const { dateFrom, dateTo } = getClockLogWeekRange(clockInLogWeekFilter);
      const filters = {};
      if (clockInLogEmployeeFilter) filters.userId = clockInLogEmployeeFilter;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const data = await getAllClockInLog(500, filters);
      setClockInLog(data);
    } catch (error) {
      console.error('Error loading clock in log:', error);
      setClockInLog([]);
    } finally {
      setLoadingClockInLog(false);
    }
  }, [clockInLogEmployeeFilter, clockInLogWeekFilter, getClockLogWeekRange]);

  const loadLetterTemplates = useCallback(async () => {
    try {
      setLoadingLetterTemplates(true);
      const data = await getLetterTemplates();
      
      // If no templates exist, use defaults
      if (!data || data.length === 0) {
        const defaults = getDefaultLetterTemplates();
        setLetterTemplates(Object.values(defaults));
      } else {
        setLetterTemplates(data);
      }
    } catch (error) {
      console.error('Error loading letter templates:', error);
      const defaults = getDefaultLetterTemplates();
      setLetterTemplates(Object.values(defaults));
    } finally {
      setLoadingLetterTemplates(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const data = await getEmailTemplates();
      
      // If no templates exist, use defaults
      if (!data || data.length === 0) {
        const defaults = getDefaultTemplates();
        setTemplates(Object.values(defaults));
      } else {
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      const defaults = getDefaultTemplates();
      setTemplates(Object.values(defaults));
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadSources = useCallback(async () => {
    try {
      setLoadingSources(true);
      const data = await getClientSources();
      setSources(data);
    } catch (error) {
      console.error('Error loading sources:', error);
      setSources([]);
    } finally {
      setLoadingSources(false);
    }
  }, []);

  const loadTaskTypes = useCallback(async () => {
    try {
      setLoadingTaskTypes(true);
      const data = await getTaskTypes(false);
      setTaskTypes(data || []);
    } catch (error) {
      console.error('Error loading task types:', error);
      setTaskTypes([]);
    } finally {
      setLoadingTaskTypes(false);
    }
  }, []);

  const loadAuditLog = useCallback(async (page = 1, filters = null) => {
    const f = filters ?? auditFilters;
    try {
      setLoadingAuditLog(true);
      const { data, total } = await getAuditLogFiltered({
        search: (f.search || '').trim() || undefined,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
        entityType: f.entityType || undefined,
        action: f.action || undefined,
        page,
        limit: AUDIT_PAGE_SIZE,
        asc: false,
      });
      setAuditLog(data || []);
      setAuditTotal(total ?? 0);
      setAuditPage(page);
    } catch (error) {
      console.error('Error loading audit log:', error);
      setAuditLog([]);
      setAuditTotal(0);
    } finally {
      setLoadingAuditLog(false);
    }
  }, [auditFilters]);

  useEffect(() => {
    if (activeTab === 'templates') {
      if (templateSubTab === 'email') {
        loadTemplates();
      } else if (templateSubTab === 'letter') {
        loadLetterTemplates();
      } else if (templateSubTab === 'order') {
        loadOrderFormTemplate();
      }
    } else if (activeTab === 'client-settings') {
      loadSources();
    } else if (activeTab === 'task-types') {
      loadTaskTypes();
    } else if (activeTab === 'audit-log') {
      loadAuditLog(1);
    } else if (activeTab === 'settings') {
      loadDefaultTaxRate();
      loadEnableDueDates();
    } else if (activeTab === 'clock-in-log') {
      loadClockInLog();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadAuditLog intentionally omitted to avoid refetch on filter change
  }, [activeTab, templateSubTab, loadOrderFormTemplate, loadDefaultTaxRate, loadEnableDueDates, loadClockInLog, loadLetterTemplates, loadTemplates, loadSources, loadTaskTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (editingUser) {
        // Editing existing user
        if (!formData.email) {
          setError('Email is required');
          setLoading(false);
          return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          setLoading(false);
          return;
        }

        // Only require password if it's being changed
        if (formData.password && formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        // Check if updating another user's password (not supported without backend)
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser?.id !== editingUser.id && formData.password) {
          setError('Password updates for other users require a backend endpoint. Only name and role can be updated.');
          setLoading(false);
          return;
        }

        const updateData = {
          full_name: formData.full_name,
          role: formData.role,
        };
        
        // Include password if provided (only works for current user)
        if (formData.password && formData.password.length >= 6 && currentUser?.id === editingUser.id) {
          updateData.password = formData.password;
        }
        
        const result = await updateUser(editingUser.id, updateData);
        
        setSuccess(`User ${formData.email} has been updated successfully!`);
        setTimeout(() => setSuccess(''), 5000);
        
        // Refresh user list with updated data
        if (result?.user) {
          setUsers(prevUsers => 
            prevUsers.map(u => u.id === result.user.id ? result.user : u)
          );
        } else {
          // Fallback: reload all users
          await loadUsers();
        }
        
        // Also update the auth context user if it's the current user
        if (user?.id === editingUser.id) {
          // The auth context will automatically update via onAuthStateChange
          // No need to manually refresh - AuthContext handles this automatically
        }
      } else {
        // Creating new user
        if (!formData.email || !formData.password) {
          setError('Email and password are required');
          setLoading(false);
          return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          setLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        const result = await createUser(formData);
        setSuccess(`User ${formData.email} has been created successfully! They will receive an email to verify their account.`);
        
        // Add the newly created user to the list if available
        if (result?.user) {
          setUsers(prev => {
            // Check if user already exists in list
            const exists = prev.some(u => u.id === result.user.id);
            if (exists) {
              return prev.map(u => u.id === result.user.id ? result.user : u);
            }
            return [...prev, result.user];
          });
        } else {
          // Refresh user list as fallback
          await loadUsers();
        }
      }

      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'tech',
      });
      setEditingUser(null);
      setShowAddUser(false);
    } catch (error) {
      const errorMessage = error.message || (editingUser ? 'Failed to update user' : 'Failed to create user');
      setError(errorMessage);
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions - defined early so they can be used by handlers
  const extractVariableName = (variable) => {
    return variable.replace(/[{}]/g, '');
  };

  const isEmptyContent = (content) => {
    if (!content || typeof content !== 'string') return true;
    const trimmed = content.trim();
    // Check for empty, or just whitespace/empty HTML tags
    if (!trimmed || 
        trimmed === '' || 
        trimmed === '<p><br></p>' || 
        trimmed === '<p></p>' ||
        trimmed === '<br>' ||
        /^<p\s*>\s*<\/p>$/i.test(trimmed) ||
        /^<p\s*><br\s*\/?>\s*<\/p>$/i.test(trimmed)) {
      return true;
    }
    // Also check if it's the old default invoice template content (treat as empty for preview)
    const oldDefaultPattern = /{{document_type}}.*{{due_date}}.*{{items_table}}.*{{totals_section}}.*{{notes_section}}/s;
    if (oldDefaultPattern.test(trimmed)) {
      return true; // Treat old default as empty for invoice preview
    }
    return false;
  };

  const convertVariablesToVisual = (content, forPreview = false) => {
    if (isEmptyContent(content)) {
      return forPreview ? '' : '<p><br></p>';
    }
    
    let result = content;
    
    // Check if content has complex HTML (tables, etc.)
    const hasComplexHTML = result.includes('<table') || result.includes('<tbody') || 
                          result.includes('<tr') || result.includes('<td') ||
                          result.includes('<th') || result.includes('<colgroup');
    
    // If content contains newlines but no HTML tags, convert newlines to <p> tags
    // This handles default templates that are plain text
    // Skip this if we have complex HTML - preserve it as-is
    if (!hasComplexHTML && result.includes('\n') && !result.includes('<')) {
      // Convert newlines to paragraphs
      result = result
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => `<p>${line}</p>`)
        .join('');
      
      // If no paragraphs were created, create one empty paragraph
      if (!result) {
        result = forPreview ? '' : '<p><br></p>';
      }
    }
    
    // Replace {{variable}} with visual merge token spans (displays as [Client.Name])
    // NEW SYSTEM: Uses data-key attribute and token display format
    // This regex works in both plain text and HTML contexts
    // Must handle variables that might be in HTML tags or plain text
    // For complex HTML, we preserve the structure and just convert variables
    result = result.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
      // Trim whitespace from variable name
      const cleanVarName = varName.trim();
      if (!cleanVarName) return match; // Skip empty variable names
      
      // Map key to token format (e.g., "client.name" -> "[Client.Name]")
      const keyToToken = (key) => {
        return key
          .split('.')
          .map(part => part
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(''))
          .join('.');
      };
      
      const token = `[${keyToToken(cleanVarName)}]`;
      
      // Use merge-token class with data-key attribute (same as Quill Embed blot)
      // Escape any HTML in the variable name to prevent XSS
      const escapedKey = cleanVarName.replace(/[<>"]/g, '');
      return `<span class="merge-token" data-key="${escapedKey}" data-token="${token}" contenteditable="false" spellcheck="false">${token}</span>`;
    });
    
    // If result is empty or just whitespace
    const trimmed = result.trim();
    if (!trimmed) {
      return forPreview ? '' : '<p><br></p>';
    }
    
    return trimmed;
  };

  const convertVariablesToStorage = (html) => {
    if (!html) return html;
    if (typeof document === 'undefined') return html; // SSR safety
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Find all merge token spans (new format - uses data-key attribute)
    const mergeTokens = tempDiv.querySelectorAll('.merge-token[data-key]');
    mergeTokens.forEach(span => {
      const key = span.getAttribute('data-key');
      if (key) {
        // Convert data-key to {{variable}} format for backend storage
        const textNode = document.createTextNode(`{{${key}}}`);
        span.parentNode.replaceChild(textNode, span);
      }
    });
    
    // Find all variable spans (old format - ql-variable)
    const variableSpans = tempDiv.querySelectorAll('.ql-variable[data-variable]');
    variableSpans.forEach(span => {
      const varName = span.getAttribute('data-variable');
      if (varName) {
        const textNode = document.createTextNode(`{{${varName}}}`);
        span.parentNode.replaceChild(textNode, span);
      }
    });
    
    // Also handle any remaining {{variable}} patterns that might not have been converted
    let result = tempDiv.innerHTML;
    // This regex will catch any {{variable}} that's already in the format (old or new)
    // Updated to handle both data-key (new) and data-variable/data-merge-token (old)
    result = result.replace(/<span[^>]*class="(ql-variable|merge-token)"[^>]*data-(variable|merge-token|key)="([^"]+)"[^>]*>.*?<\/span>/g, '{{$3}}');
    
    return result;
  };

  const handleEditTemplate = (template) => {
    if (!template) return;
    setEditingTemplate(template);
    setTemplateFormData({ 
      template_type: template?.template_type || '',
      subject: template?.subject || '', 
      body: template?.body || '' 
    });
    setTemplateError('');
    setTemplateSuccess('');
  };

  const handleTemplateFormChange = (e) => {
    const { name, value } = e.target;
    setTemplateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setTemplateError('');
    setTemplateSuccess('');

    try {
      await saveEmailTemplate(templateFormData);
      await loadTemplates();
      setEditingTemplate(null);
      setTemplateSuccess('Template saved successfully!');
      setTimeout(() => {
        setTemplateSuccess('');
      }, 3000);
    } catch (error) {
      setTemplateError(error.message || 'Failed to save template');
    }
  };

  const handleResetTemplate = () => {
    if (window.confirm('Are you sure you want to reset this template to its default?')) {
      const defaultTemplates = getDefaultTemplates();
      const defaultTemplate = defaultTemplates[editingTemplate.template_type];
      if (defaultTemplate) {
        setTemplateFormData({ 
          template_type: editingTemplate.template_type,
          subject: defaultTemplate.subject, 
          body: defaultTemplate.body 
        });
        setTemplateSuccess('Template reset to default. Click Save to apply.');
      }
    }
  };


  const insertVariable = (field, variable) => {
    const textarea = document.querySelector(`textarea[name="${field}"]`);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateFormData[field];
      const newText = text.substring(0, start) + variable + text.substring(end);
      
      setTemplateFormData({
        ...templateFormData,
        [field]: newText,
      });

      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = start + variable.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const handleSourceSubmit = async (e) => {
    e.preventDefault();
    setSourceError('');
    setSourceSuccess('');

    try {
      if (!sourceFormData.name || sourceFormData.name.trim() === '') {
        setSourceError('Source name is required');
        return;
      }

      if (editingSource) {
        await updateClientSource(editingSource.id, sourceFormData);
        setSourceSuccess('Source updated successfully!');
      } else {
        await createClientSource(sourceFormData);
        setSourceSuccess('Source created successfully!');
      }

      await loadSources();
      setShowAddSource(false);
      setEditingSource(null);
      setSourceFormData({ name: '' });
      setTimeout(() => setSourceSuccess(''), 3000);
    } catch (error) {
      setSourceError(error.message || 'Failed to save source');
    }
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setSourceFormData({ name: source.name });
    setShowAddSource(true);
    setSourceError('');
    setSourceSuccess('');
  };

  const handleDeleteSource = async (sourceId) => {
    if (window.confirm('Are you sure you want to delete this source? This will remove it from all clients using it.')) {
      try {
        await deleteClientSource(sourceId);
        await loadSources();
        setSourceSuccess('Source deleted successfully!');
        setTimeout(() => setSourceSuccess(''), 3000);
      } catch (error) {
        setSourceError(error.message || 'Failed to delete source');
      }
    }
  };

  const handleTaskTypeSubmit = async (e) => {
    e.preventDefault();
    setTaskTypeError('');
    setTaskTypeSuccess('');
    const code = (taskTypeFormData.code || taskTypeFormData.name.toLowerCase().replace(/\s+/g, '_')).trim();
    if (!code) {
      setTaskTypeError('Name or code is required.');
      return;
    }
    try {
      const payload = {
        name: taskTypeFormData.name.trim() || code,
        code,
        description: taskTypeFormData.description?.trim() || null,
        default_status: taskTypeFormData.default_status || 'scheduled',
        display_order: Number(taskTypeFormData.display_order) || 0,
        active: taskTypeFormData.active,
        requires_completion_modal: taskTypeFormData.requires_completion_modal,
        completion_actions: {
          allow_completion_note: taskTypeFormData.allow_completion_note,
          allow_create_follow_up: taskTypeFormData.allow_create_follow_up,
          completion_note_label: taskTypeFormData.completion_note_label || undefined,
          completion_note_placeholder: taskTypeFormData.completion_note_placeholder || undefined,
          follow_up_label: taskTypeFormData.follow_up_label || undefined,
          follow_up_hint: taskTypeFormData.follow_up_hint || undefined,
          require_arrival_time: taskTypeFormData.require_arrival_time || undefined,
          require_start_time: taskTypeFormData.require_start_time || undefined,
          require_completion_note: taskTypeFormData.require_completion_note || undefined,
          allow_reschedule_needed: taskTypeFormData.allow_reschedule_needed || undefined,
          require_note_when_reschedule: taskTypeFormData.require_note_when_reschedule || undefined,
          requires_measurements: taskTypeFormData.requires_measurements || undefined,
          link_to_invoice: taskTypeFormData.link_to_invoice || undefined,
          hide_edit_button: taskTypeFormData.hide_edit_button || undefined,
          hide_completion_button: taskTypeFormData.hide_completion_button || undefined,
          hide_delete_button: taskTypeFormData.hide_delete_button || undefined,
          hide_complete_button: taskTypeFormData.hide_complete_button || undefined,
        },
      };
      if (editingTaskType) {
        await updateTaskType(editingTaskType.id, payload);
        setTaskTypeSuccess('Task type updated successfully!');
      } else {
        await createTaskType(payload);
        setTaskTypeSuccess('Task type created successfully!');
      }
      await loadTaskTypes();
      setShowAddTaskType(false);
      setEditingTaskType(null);
        setTaskTypeFormData({
        name: '',
        code: '',
        description: '',
        default_status: 'scheduled',
        display_order: 0,
        active: true,
        requires_completion_modal: false,
        allow_completion_note: true,
        allow_create_follow_up: false,
        completion_note_label: 'Completion Notes',
        completion_note_placeholder: 'Add any notes about task completion...',
        follow_up_label: 'Create Follow-Up Task',
        follow_up_hint: 'Check this to automatically create a follow-up task after completing this one.',
        require_arrival_time: false,
        require_start_time: false,
        require_completion_note: false,
        allow_reschedule_needed: false,
        require_note_when_reschedule: false,
        requires_measurements: false,
        link_to_invoice: false,
        hide_edit_button: false,
        hide_completion_button: false,
        hide_delete_button: false,
        hide_complete_button: false,
      });
      setTimeout(() => setTaskTypeSuccess(''), 3000);
    } catch (error) {
      setTaskTypeError(error.message || 'Failed to save task type');
    }
  };

  const handleEditTaskType = (taskType) => {
    setEditingTaskType(taskType);
    const actions = taskType.completion_actions || {};
    setTaskTypeFormData({
      name: taskType.name || '',
      code: taskType.code || '',
      description: taskType.description || '',
      default_status: taskType.default_status || 'scheduled',
      display_order: taskType.display_order ?? 0,
      active: taskType.active !== false,
      requires_completion_modal: taskType.requires_completion_modal === true,
      allow_completion_note: actions.allow_completion_note !== false,
      allow_create_follow_up: actions.allow_create_follow_up === true,
      completion_note_label: actions.completion_note_label ?? 'Completion Notes',
      completion_note_placeholder: actions.completion_note_placeholder ?? 'Add any notes about task completion...',
      follow_up_label: actions.follow_up_label ?? 'Create Follow-Up Task',
      follow_up_hint: actions.follow_up_hint ?? 'Check this to automatically create a follow-up task after completing this one.',
      require_arrival_time: actions.require_arrival_time === true,
      require_start_time: actions.require_start_time === true,
      require_completion_note: actions.require_completion_note === true,
      allow_reschedule_needed: actions.allow_reschedule_needed === true,
      require_note_when_reschedule: actions.require_note_when_reschedule === true,
      requires_measurements: actions.requires_measurements === true,
      link_to_invoice: actions.link_to_invoice === true,
      hide_edit_button: actions.hide_edit_button === true,
      hide_completion_button: actions.hide_completion_button === true,
      hide_delete_button: actions.hide_delete_button === true,
      hide_complete_button: actions.hide_complete_button === true,
    });
    setShowAddTaskType(true);
    setTaskTypeError('');
    setTaskTypeSuccess('');
  };

  const handleDeleteTaskType = async (taskTypeId) => {
    if (window.confirm('Are you sure you want to delete this task type? Tasks using it may need to be updated.')) {
      try {
        await deleteTaskType(taskTypeId);
        await loadTaskTypes();
        setTaskTypeSuccess('Task type deleted successfully!');
        setTimeout(() => setTaskTypeSuccess(''), 3000);
      } catch (error) {
        setTaskTypeError(error.message || 'Failed to delete task type');
      }
    }
  };

  const handleEditCompletionModal = (taskType) => {
    const actions = taskType.completion_actions || {};
    setEditingCompletionModalTaskType(taskType);
    setCompletionModalFormData({
      requires_completion_modal: taskType.requires_completion_modal === true,
      allow_completion_note: actions.allow_completion_note !== false,
      allow_create_follow_up: actions.allow_create_follow_up === true,
      completion_note_label: actions.completion_note_label ?? 'Completion Notes',
      completion_note_placeholder: actions.completion_note_placeholder ?? 'Add any notes about task completion...',
      follow_up_label: actions.follow_up_label ?? 'Create Follow-Up Task',
      follow_up_hint: actions.follow_up_hint ?? 'Check this to automatically create a follow-up task after completing this one.',
      require_arrival_time: actions.require_arrival_time === true,
      require_start_time: actions.require_start_time === true,
      require_completion_note: actions.require_completion_note === true,
      allow_reschedule_needed: actions.allow_reschedule_needed === true,
      require_note_when_reschedule: actions.require_note_when_reschedule === true,
      requires_measurements: actions.requires_measurements === true,
      link_to_invoice: actions.link_to_invoice === true,
      hide_edit_button: actions.hide_edit_button === true,
      hide_completion_button: actions.hide_completion_button === true,
      hide_delete_button: actions.hide_delete_button === true,
      hide_complete_button: actions.hide_complete_button === true,
    });
    setCompletionModalSaveError('');
    setCompletionModalSaveSuccess('');
  };

  const handleCompletionModalUpdate = async (e) => {
    e.preventDefault();
    if (!editingCompletionModalTaskType) return;
    setCompletionModalSaveError('');
    try {
      await updateTaskType(editingCompletionModalTaskType.id, {
        requires_completion_modal: completionModalFormData.requires_completion_modal,
        completion_actions: {
          allow_completion_note: completionModalFormData.allow_completion_note,
          allow_create_follow_up: completionModalFormData.allow_create_follow_up,
          completion_note_label: completionModalFormData.completion_note_label || undefined,
          completion_note_placeholder: completionModalFormData.completion_note_placeholder || undefined,
          follow_up_label: completionModalFormData.follow_up_label || undefined,
          follow_up_hint: completionModalFormData.follow_up_hint || undefined,
          require_arrival_time: completionModalFormData.require_arrival_time || undefined,
          require_start_time: completionModalFormData.require_start_time || undefined,
          require_completion_note: completionModalFormData.require_completion_note || undefined,
          allow_reschedule_needed: completionModalFormData.allow_reschedule_needed || undefined,
          require_note_when_reschedule: completionModalFormData.require_note_when_reschedule || undefined,
          requires_measurements: completionModalFormData.requires_measurements || undefined,
          link_to_invoice: completionModalFormData.link_to_invoice || undefined,
          hide_edit_button: completionModalFormData.hide_edit_button || undefined,
          hide_completion_button: completionModalFormData.hide_completion_button || undefined,
          hide_delete_button: completionModalFormData.hide_delete_button || undefined,
          hide_complete_button: completionModalFormData.hide_complete_button || undefined,
        },
      });
      await loadTaskTypes();
      setCompletionModalSaveSuccess('Completion modal settings updated.');
      setTimeout(() => {
        setEditingCompletionModalTaskType(null);
        setCompletionModalSaveSuccess('');
      }, 1500);
    } catch (error) {
      setCompletionModalSaveError(error.message || 'Failed to update completion modal settings');
    }
  };

  const handleEditOrderFormTemplate = () => {
    setEditingOrderFormTemplate(true);
    setOrderFormTemplateError('');
    setOrderFormTemplateSuccess('');
  };

  const handleOrderFormTemplateSubmit = async (e) => {
    e.preventDefault();
    setOrderFormTemplateError('');
    setOrderFormTemplateSuccess('');

    try {
      await saveOrderFormTemplate(orderFormTemplateFormData);
      await loadOrderFormTemplate();
      setEditingOrderFormTemplate(false);
      setOrderFormTemplateSuccess('Template saved successfully!');
      setTimeout(() => {
        setOrderFormTemplateSuccess('');
      }, 3000);
    } catch (error) {
      setOrderFormTemplateError(error.message || 'Failed to save template');
    }
  };

  const handleResetOrderFormTemplate = () => {
    if (window.confirm('Are you sure you want to reset this template to its default?')) {
      const defaultTemplate = getDefaultOrderFormTemplate();
      setOrderFormTemplateFormData({
        subject: defaultTemplate.subject || '',
        body: defaultTemplate.body || '',
      });
      setOrderFormTemplateSuccess('Template reset to default. Click Save to apply.');
    }
  };

  const insertOrderFormVariable = (variable) => {
    const textarea = document.querySelector('textarea[name="body"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = orderFormTemplateFormData.body;
      const newText = text.substring(0, start) + variable + text.substring(end);
      
      setOrderFormTemplateFormData({
        ...orderFormTemplateFormData,
        body: newText,
      });

      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = start + variable.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const handleEditLetterTemplate = (template) => {
    if (!template) return;
    setEditingLetterTemplate(template);
    setLetterTemplateError('');
    setLetterTemplateSuccess('');
    setIsPreviewMode(false);
    
    // Load header, body, and footer separately
    // Convert to visual format for editing
    const header = template?.header || '';
    const body = template?.body || '';
    const footer = template?.footer || '';
    
    const headerVisual = header ? convertVariablesToVisual(header, false) : '';
    const bodyVisual = body ? convertVariablesToVisual(body, false) : '';
    const footerVisual = footer ? convertVariablesToVisual(footer, false) : '';
    
    setLetterTemplateFormData({
      template_type: template?.template_type || '',
      header: headerVisual,
      body: bodyVisual,
      footer: footerVisual,
      document: '', // Not used when using separate editors
    });
  };

  const handleLetterTemplateQuillChange = (field, value) => {
    // Keep the HTML value as-is for ReactQuill to display
    // We'll convert to storage format only when saving
    setLetterTemplateFormData(prev => ({ ...prev, [field]: value || '' }));
  };

  const handleLetterTemplateSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setLetterTemplateError('');
    setLetterTemplateSuccess('');

    try {
      // Convert visual format to storage format before saving
      // Save header, body, and footer separately (preserving existing structure)
      const headerContent = convertVariablesToStorage(letterTemplateFormData.header || '');
      const bodyContent = convertVariablesToStorage(letterTemplateFormData.body || '');
      const footerContent = convertVariablesToStorage(letterTemplateFormData.footer || '');
      
      const storageData = {
        template_type: letterTemplateFormData.template_type, // 'estimate' or 'invoice'
        header: headerContent,
        body: bodyContent,
        footer: footerContent,
      };
      
      await saveLetterTemplate(storageData);
      await loadLetterTemplates();
      setEditingLetterTemplate(null);
      setLetterTemplateSuccess(`${storageData.template_type === 'estimate' ? 'Estimate' : 'Invoice'} template saved successfully!`);
      setTimeout(() => {
        setLetterTemplateSuccess('');
      }, 3000);
    } catch (error) {
      setLetterTemplateError(error.message || 'Failed to save template');
    }
  };

  const handleResetLetterTemplate = () => {
    if (!editingLetterTemplate) return;
    if (window.confirm('Are you sure you want to reset this template to its default?')) {
      const defaultTemplates = getDefaultLetterTemplates();
      const defaultTemplate = defaultTemplates[editingLetterTemplate?.template_type];
      if (defaultTemplate) {
        // Reset to default with separate sections
        const headerVisual = defaultTemplate.header ? convertVariablesToVisual(defaultTemplate.header || '', false) : '';
        const bodyVisual = defaultTemplate.body ? convertVariablesToVisual(defaultTemplate.body || '', false) : '';
        const footerVisual = defaultTemplate.footer ? convertVariablesToVisual(defaultTemplate.footer || '', false) : '';
        
        setLetterTemplateFormData({
          template_type: editingLetterTemplate.template_type,
          header: headerVisual,
          body: bodyVisual,
          footer: footerVisual,
          document: '', // Not used when using separate editors
        });
        setLetterTemplateSuccess('Template reset to default. Click Save to apply.');
      }
    }
  };

  // Refs for Quill editors
  const quillRefs = {
    header: useRef(null),
    body: useRef(null),
    footer: useRef(null),
  };

  // New merge token insertion for storEDGE-style editor
  const insertMergeToken = (field, variable) => {
    // variable = { key: "client.name", token: "[Client.Name]", label: "Client Name" }
    // The DocumentEditor component handles the actual insertion via Quill
    // This is called by TemplateEditorLayout which manages the editor ref
    // No action needed here - the editor ref is managed in TemplateEditorLayout
  };

  // Legacy variable insertion (kept for backward compatibility)
  const insertLetterVariable = (field, variable) => {
    const quill = quillRefs[field]?.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      if (range) {
        // Extract variable name from {{variable_name}} format
        const variableName = extractVariableName(variable);
        
        // Insert as custom variable blot
        quill.insertText(range.index, variableName, 'variable', variableName, 'user');
        quill.setSelection(range.index + variableName.length + 2); // +2 for brackets
      }
    }
  };

  // Quill toolbar configuration with color, size, and alignment
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ],
    },
    clipboard: {
      // Preserve HTML structure even if Quill doesn't understand it
      matchVisual: false,
    },
  };

  const quillFormats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'blockquote', 'code-block',
    'list', 'bullet',
    'link',
    'variable', // Custom variable format
    // Allow additional HTML tags that might be in templates
    'table', 'tr', 'td', 'th', 'tbody', 'thead', 'tfoot',
    'span', 'div', 'p', 'br', 'strong', 'em', 'u', 's',
    'font', 'sub', 'sup', 'strike', 'code'
  ];

  const availableVariables = [
    { name: 'Invoice/Estimate Number', value: '{{invoice_number}}', description: 'The document number' },
    { name: 'Client Name', value: '{{client_name}}', description: 'The client\'s full name' },
    { name: 'Total Amount', value: '{{total_amount}}', description: 'Total amount (formatted as currency)' },
    { name: 'Due Date', value: '{{due_date}}', description: 'Due date (formatted, receipt only)' },
    { name: 'Due Date Section', value: '{{due_date_section}}', description: 'Due date line (only shown if exists, receipt only)' },
    { name: 'Payment Method', value: '{{payment_method}}', description: 'Payment method (e.g., Visa, Mastercard, receipt only)' },
    { name: 'Payment Method Section', value: '{{payment_method_section}}', description: 'Payment method line (only shown if exists, receipt only)' },
    { name: 'Card Number', value: '{{card_number}}', description: 'Full card number (receipt only)' },
    { name: 'Card Number Section', value: '{{card_number_section}}', description: 'Masked card number line (only shown if exists, receipt only)' },
  ];

  const orderFormVariables = [
    { name: 'Our P.O.', value: '{{our_po}}', description: 'Our P.O. / job name' },
    { name: 'Order Number', value: '{{order_number}}', description: 'Order number (use invoice number when "Use invoice number as order number" is selected; link to invoice is in the software)' },
    { name: 'Order Number Link', value: '{{order_number_link}}', description: 'Not used; no invoice link is included in the email.' },
    { name: 'To', value: '{{to}}', description: 'Recipient / client name' },
    { name: 'Sales Associate', value: '{{sales_associate}}', description: 'Current user name' },
    { name: 'Order Date', value: '{{order_date}}', description: 'Order date (formatted)' },
    { name: 'Description', value: '{{description}}', description: 'Item description' },
    { name: 'Quantity / Dimensions / Thickness', value: '{{quantity_dimensions}}', description: 'Quantity and dimensions line' },
    { name: 'Specifications', value: '{{specifications}}', description: 'Specifications' },
    { name: 'Comments', value: '{{comments}}', description: 'Comments' },
    { name: 'Client Name', value: '{{client_name}}', description: 'The client\'s full name' },
    { name: 'Client Email', value: '{{client_email}}', description: 'The client\'s email address' },
    { name: 'Client Phone', value: '{{client_phone}}', description: 'The client\'s phone number' },
    { name: 'Client Address', value: '{{client_address}}', description: 'The client\'s street address' },
    { name: 'Client Address Section', value: '{{client_address_section}}', description: 'Address line (only shown if address exists)' },
    { name: 'Client City/State/Zip', value: '{{client_city_state_zip}}', description: 'City, state, and zip code' },
    { name: 'Client City/State/Zip Section', value: '{{client_city_state_zip_section}}', description: 'City/state/zip line (only shown if data exists)' },
    { name: 'Glass Type', value: '{{glass_type}}', description: 'Type of glass ordered' },
    { name: 'Dimensions', value: '{{dimensions}}', description: 'Glass dimensions' },
    { name: 'Quantity', value: '{{quantity}}', description: 'Quantity ordered' },
    { name: 'Thickness', value: '{{thickness}}', description: 'Glass thickness' },
    { name: 'Edge Work', value: '{{edge_work}}', description: 'Edge work specifications' },
    { name: 'Holes / Cuts', value: '{{holes_cuts}}', description: 'Holes and cuts information' },
    { name: 'Priority', value: '{{priority}}', description: 'Order priority (Normal, Rush, Urgent)' },
    { name: 'Priority Section', value: '{{priority_section}}', description: 'Priority section (only shown if priority is not Normal)' },
    { name: 'Delivery Date', value: '{{delivery_date}}', description: 'Delivery date (formatted)' },
    { name: 'Delivery Date Section', value: '{{delivery_date_section}}', description: 'Delivery date line (only shown if date exists)' },
    { name: 'Special Instructions', value: '{{special_instructions}}', description: 'Special instructions or notes' },
  ];

  const letterTemplateVariables = [
    { name: 'Document Type', value: '{{document_type}}', description: 'The type of document (Estimate, Invoice, etc.)' },
    { name: 'Invoice/Estimate Number', value: '{{invoice_number}}', description: 'The document number' },
    { name: 'Date', value: '{{date}}', description: 'Document date (formatted)' },
    { name: 'Due Date', value: '{{due_date}}', description: 'Due date (formatted, invoice only)' },
    { name: 'User Name', value: '{{user_name}}', description: 'The logged-in user\'s name' },
    { name: 'Client Name', value: '{{client_name}}', description: 'The client\'s full name' },
    { name: 'Client Address', value: '{{client_address}}', description: 'The client\'s street address' },
    { name: 'Client City/State/Zip', value: '{{client_city_state_zip}}', description: 'City, state, and zip code' },
    { name: 'Items Table', value: '{{items_table}}', description: 'Table of line items' },
    { name: 'Totals Section', value: '{{totals_section}}', description: 'Subtotal, tax, and total amounts' },
    { name: 'Notes Section', value: '{{notes_section}}', description: 'Notes or additional information' },
  ];

  const formatAuditAction = (action, entityType) => {
    const actionMap = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      mark_paid: 'Marked as Paid',
      finalize: 'Finalized',
    };
    const entityMap = {
      client: 'Client',
      invoice: 'Invoice',
      estimate: 'Estimate',
    };
    return `${actionMap[action] || action} ${entityMap[entityType] || entityType}`;
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`settings-tab ${activeTab === 'client-settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('client-settings')}
        >
          Client Settings
        </button>
        <button
          className={`settings-tab ${activeTab === 'task-types' ? 'active' : ''}`}
          onClick={() => setActiveTab('task-types')}
        >
          Task Types
        </button>
        <button
          className={`settings-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Template Settings
        </button>
        <button
          className={`settings-tab ${activeTab === 'audit-log' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit-log')}
        >
          Audit Log
        </button>
        <button
          className={`settings-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Application Settings
        </button>
        {isAdminOrManager(user) && (
          <button
            className={`settings-tab ${activeTab === 'clock-in-log' ? 'active' : ''}`}
            onClick={() => setActiveTab('clock-in-log')}
          >
            Clock In/Out Log
          </button>
        )}
      </div>

      <div className="settings-content">
        {templateSuccess && (
          <div className="success-message" style={{ marginBottom: '1.5rem' }}>
            {templateSuccess}
          </div>
        )}
        {sourceSuccess && (
          <div className="success-message" style={{ marginBottom: '1.5rem' }}>
            {sourceSuccess}
          </div>
        )}
        {taskTypeSuccess && (
          <div className="success-message" style={{ marginBottom: '1.5rem' }}>
            {taskTypeSuccess}
          </div>
        )}
        {letterTemplateSuccess && (
          <div className="success-message" style={{ marginBottom: '1.5rem' }}>
            {letterTemplateSuccess}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Users</h2>
              <button 
                className="btn-primary" 
                onClick={() => {
                  setEditingUser(null);
                  setFormData({ email: '', password: '', full_name: '', role: 'tech' });
                  setError('');
                  setSuccess('');
                  setShowAddUser(true);
                }}
                aria-label="Add new user"
              >
                + Add User
              </button>
            </div>
            <p className="section-description">
              Manage users who have access to the system.
            </p>

            {error && (
              <div className="error-message" style={{ marginBottom: '1rem' }} role="alert">{error}</div>
            )}
            {success && (
              <div className="success-message" style={{ marginBottom: '1rem' }} role="status">{success}</div>
            )}

            <div className="users-list">
              {loading ? (
                <div className="loading-state">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="empty-state">
                  <p>No users found</p>
                </div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td>{u.user_metadata?.full_name || '-'}</td>
                        <td>
                          <span className="role-badge">
                            {({ tech: 'Tech', user: 'Tech', manager: 'Manager', admin: 'Admin' }[u.user_metadata?.role] || u.user_metadata?.role || 'Tech')}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${u.email_confirmed_at ? 'active' : 'pending'}`}>
                            {u.email_confirmed_at ? 'Active' : 'Pending'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-edit"
                            onClick={() => {
                              setEditingUser(u);
                              const role = u.user_metadata?.role || 'tech';
                              setFormData({
                                email: u.email,
                                password: '',
                                full_name: u.user_metadata?.full_name || '',
                                role: role === 'user' ? 'tech' : role,
                              });
                              setShowAddUser(true);
                              setError('');
                              setSuccess('');
                            }}
                            aria-label={`Edit user ${u.email}`}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="info-box">
              <h3>Note</h3>
              <p>
                New users will receive an email to verify their account. 
                They must verify their email before they can log in.
              </p>
            </div>
          </div>
        )}

        {/* Client Settings Tab */}
        {activeTab === 'client-settings' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Client Sources</h2>
              <button 
                className="btn-primary" 
                onClick={() => {
                  setShowAddSource(true);
                  setEditingSource(null);
                  setSourceFormData({ name: '' });
                  setSourceError('');
                  setSourceSuccess('');
                }}
                aria-label="Add new client source"
              >
                + Add Source
              </button>
            </div>
            <p className="section-description">
              Manage client sources that can be selected when creating clients.
            </p>

            {loadingSources ? (
              <div className="loading-state">Loading sources...</div>
            ) : (
              <div className="sources-list">
                {sources.length === 0 ? (
                  <div className="empty-state">
                    <p>No sources found. Add your first source to get started.</p>
                  </div>
                ) : (
                  <table className="sources-table">
                    <thead>
                      <tr>
                        <th>Source Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.map((source) => (
                        <tr key={source.id}>
                          <td>{source.name}</td>
                          <td>
                            <button
                              className="btn-edit"
                              onClick={() => handleEditSource(source)}
                              aria-label={`Edit source ${source.name}`}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleDeleteSource(source.id)}
                              aria-label={`Delete source ${source.name}`}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Task Types Tab */}
        {activeTab === 'task-types' && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Task Types</h2>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowAddTaskType(true);
                  setEditingTaskType(null);
                  setTaskTypeFormData({
                    name: '',
                    code: '',
                    description: '',
                    default_status: 'scheduled',
                    display_order: taskTypes.length,
                    active: true,
                    requires_completion_modal: false,
                    allow_completion_note: true,
                    allow_create_follow_up: false,
                    completion_note_label: 'Completion Notes',
                    completion_note_placeholder: 'Add any notes about task completion...',
                    follow_up_label: 'Create Follow-Up Task',
                    follow_up_hint: 'Check this to automatically create a follow-up task after completing this one.',
                    require_arrival_time: false,
                    require_start_time: false,
                    hide_edit_button: false,
                    hide_completion_button: false,
                    hide_delete_button: false,
                    hide_complete_button: false,
                  });
                  setTaskTypeError('');
                  setTaskTypeSuccess('');
                }}
                aria-label="Add new task type"
              >
                + Add Task Type
              </button>
            </div>
            <p className="section-description">
              Manage task types used when scheduling tasks. Completion behavior (e.g. completion notes, creating a follow-up task) can be configured per type.
            </p>

            {loadingTaskTypes ? (
              <div className="loading-state">Loading task types...</div>
            ) : (
              <div className="sources-list">
                {taskTypes.length === 0 ? (
                  <div className="empty-state">
                    <p>No task types found. Add your first task type to get started.</p>
                  </div>
                ) : (
                  <table className="sources-table task-types-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Completion modal</th>
                        <th>Completion note</th>
                        <th>Create follow-up</th>
                        <th>Arrival time</th>
                        <th>Start time</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskTypes.map((tt) => {
                        const actions = tt.completion_actions || {};
                        return (
                          <tr key={tt.id}>
                            <td>{tt.name}</td>
                            <td><code className="code-cell">{tt.code}</code></td>
                            <td>{tt.requires_completion_modal ? 'Yes' : 'No'}</td>
                            <td>{actions.allow_completion_note !== false ? 'Yes' : 'No'}</td>
                            <td>{actions.allow_create_follow_up ? 'Yes' : 'No'}</td>
                            <td>{actions.require_arrival_time ? 'Yes' : 'No'}</td>
                            <td>{actions.require_start_time ? 'Yes' : 'No'}</td>
                            <td>{tt.active ? 'Yes' : 'No'}</td>
                            <td>
                              <div className="task-type-actions">
                                {!(actions.hide_edit_button) && (
                                  <button
                                    className="btn-edit"
                                    onClick={() => handleEditTaskType(tt)}
                                    aria-label={`Edit task type ${tt.name}`}
                                  >
                                    Edit
                                  </button>
                                )}
                                {!(actions.hide_completion_button) && (
                                  <button
                                    className="btn-edit"
                                    onClick={() => handleEditCompletionModal(tt)}
                                    aria-label={`Edit completion modal for ${tt.name}`}
                                    title="Edit completion modal"
                                  >
                                    Completion
                                  </button>
                                )}
                                {!(actions.hide_delete_button) && (
                                  <button
                                    className="btn-delete"
                                    onClick={() => handleDeleteTaskType(tt.id)}
                                    aria-label={`Delete task type ${tt.name}`}
                                  >
                                    Delete
                                  </button>
                                )}
                                {actions.hide_edit_button && actions.hide_completion_button && actions.hide_delete_button && (
                                  <span className="text-muted" style={{ fontSize: '0.875rem' }}>—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Template Settings Tab */}
        {activeTab === 'templates' && (
          <div className="settings-section">
            <h2>Template Settings</h2>
            <p className="section-description">
              Customize email and letter templates used when sending quotes and invoices to clients.
              Only users with admin or manager roles can access and modify these templates.
            </p>

            {/* Template Sub-tabs */}
            <div className="template-sub-tabs">
              <button
                className={`template-sub-tab ${templateSubTab === 'email' ? 'active' : ''}`}
                onClick={() => setTemplateSubTab('email')}
              >
                Email Templates
              </button>
              <button
                className={`template-sub-tab ${templateSubTab === 'letter' ? 'active' : ''}`}
                onClick={() => setTemplateSubTab('letter')}
              >
                Letter Templates
              </button>
              <button
                className={`template-sub-tab ${templateSubTab === 'order' ? 'active' : ''}`}
                onClick={() => setTemplateSubTab('order')}
              >
                Order Form Template
              </button>
            </div>

            {/* Email Templates Sub-tab */}
            {templateSubTab === 'email' && (
              <div>
                {loadingTemplates ? (
                  <div className="loading-state">Loading templates...</div>
                ) : (
                  <div className="templates-list">
                    {templates.map((template) => {
                      let templateName = 'Email Template';
                      if (template.template_type === 'estimate') {
                        templateName = 'Estimate Email';
                      } else if (template.template_type === 'unpaid_invoice') {
                        templateName = 'Unpaid Invoice Email';
                      } else if (template.template_type === 'invoice') {
                        templateName = 'Paid Invoice/Receipt Email';
                      }
                      
                      return (
                        <div key={template.template_type} className="template-card">
                          <div className="template-header">
                            <h3>{templateName}</h3>
                            <button
                              className="btn-edit-template"
                              onClick={() => handleEditTemplate(template)}
                            >
                              Edit
                            </button>
                          </div>
                          <div className="template-preview">
                            <div className="preview-item">
                              <strong>Subject:</strong>
                              <p>{template.subject || 'No subject set'}</p>
                            </div>
                            <div className="preview-item">
                              <strong>Body:</strong>
                              <p className="preview-body">{template.body || 'No body set'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Letter Templates Sub-tab */}
            {templateSubTab === 'letter' && (
              <div>
                {loadingLetterTemplates ? (
                  <div className="loading-state">Loading letter templates...</div>
                ) : (
                  <div className="templates-list">
                    {letterTemplates.map((template) => {
                      if (!template) return null;
                      let templateName = 'Letter Template';
                      if (template?.template_type === 'estimate') {
                        templateName = 'Estimate Letter';
                      } else if (template?.template_type === 'invoice') {
                        templateName = 'Invoice Letter';
                      }
                      
                      // Combine header, body, and footer into one document for preview
                      // New format stores everything in body, but check all fields for backward compatibility
                      const templateContent = template?.body || 
                        ((template?.header || '') + (template?.body || '') + (template?.footer || ''));
                      const previewContent = convertVariablesToVisual(templateContent || '', true);
                      
                      return (
                        <div key={template?.template_type || 'unknown'} className="template-card">
                          <div className="template-header">
                            <h3>{templateName}</h3>
                            <button
                              className="btn-edit-template"
                              onClick={() => handleEditLetterTemplate(template)}
                            >
                              Edit
                            </button>
                          </div>
                          {/* Google Docs-style preview */}
                          <div className="template-preview-document">
                            <div className="document-preview-page">
                              <div 
                                className="document-preview-content"
                                dangerouslySetInnerHTML={{ __html: previewContent || '<p style="color: #9ca3af; font-style: italic;">No content yet. Click Edit to create your template.</p>' }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Order Form Template Sub-tab */}
            {templateSubTab === 'order' && (
              <div>
                {loadingOrderFormTemplate ? (
                  <div className="loading-state">Loading order form template...</div>
                ) : (
                  <div className="templates-list">
                    {orderFormTemplate && (
                      <div className="template-card">
                        <div className="template-header">
                          <h3>Order Form Email Template</h3>
                          <button
                            className="btn-edit-template"
                            onClick={handleEditOrderFormTemplate}
                          >
                            Edit
                          </button>
                        </div>
                        <div className="template-preview">
                          <div className="preview-item">
                            <strong>Subject:</strong>
                            <p>{orderFormTemplate.subject || 'No subject set'}</p>
                          </div>
                          <div className="preview-item">
                            <strong>Body:</strong>
                            <p className="preview-body">{orderFormTemplate.body || 'No body set'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Order Form Template Editor Modal */}
                {editingOrderFormTemplate && orderFormTemplate && (
                  <div className="modal-overlay" onClick={() => setEditingOrderFormTemplate(false)}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                      <div className="modal-header">
                        <h2>Edit Order Form Template</h2>
                        <button className="btn-close" onClick={() => setEditingOrderFormTemplate(false)}>×</button>
                      </div>
                      <form onSubmit={handleOrderFormTemplateSubmit} className="template-form">
                        {orderFormTemplateError && (
                          <div className="error-message">{orderFormTemplateError}</div>
                        )}
                        {orderFormTemplateSuccess && (
                          <div className="success-message">{orderFormTemplateSuccess}</div>
                        )}

                        <div className="form-group">
                          <label>Email Subject *</label>
                          <input
                            type="text"
                            name="subject"
                            value={orderFormTemplateFormData.subject}
                            onChange={(e) => setOrderFormTemplateFormData({ ...orderFormTemplateFormData, subject: e.target.value })}
                            required
                            placeholder="e.g., Glass Order - {{client_name}}"
                          />
                        </div>

                        <div className="form-group">
                          <label>Email Body *</label>
                          <div className="textarea-with-variables">
                            <textarea
                              name="body"
                              value={orderFormTemplateFormData.body}
                              onChange={(e) => setOrderFormTemplateFormData({ ...orderFormTemplateFormData, body: e.target.value })}
                              required
                              rows="15"
                              placeholder="Enter the email body template..."
                            />
                            <div className="variable-selector">
                              <h4>Available Variables</h4>
                              <p className="variable-hint">Click to insert into template</p>
                              <div className="variable-buttons">
                                {orderFormVariables.map((variable) => (
                                  <button
                                    key={variable.value}
                                    type="button"
                                    className="variable-button"
                                    onClick={() => insertOrderFormVariable(variable.value)}
                                    title={variable.description}
                                  >
                                    {variable.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={handleResetOrderFormTemplate}
                          >
                            Reset to Default
                          </button>
                          <div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => setEditingOrderFormTemplate(false)}
                            >
                              Cancel
                            </button>
                            <button type="submit" className="btn-primary">
                              Save Template
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Letter Template Editor - storEDGE Style */}
            {editingLetterTemplate && (
              <div className="template-editor-page">
                <TemplateActionBar
                  templateType={editingLetterTemplate.template_type}
                  onCancel={() => setEditingLetterTemplate(null)}
                  onSave={handleLetterTemplateSubmit}
                  onReset={handleResetLetterTemplate}
                  isPreviewMode={isPreviewMode}
                  onTogglePreview={() => setIsPreviewMode(!isPreviewMode)}
                  error={letterTemplateError}
                  success={letterTemplateSuccess}
                />
                <TemplateEditorLayout
                  documentValue={assembleDocument({
                    header: letterTemplateFormData.header || '',
                    body: letterTemplateFormData.body || '',
                    footer: letterTemplateFormData.footer || '',
                  })}
                  onDocumentChange={(value) => setLetterTemplateFormData({
                    ...letterTemplateFormData,
                    header: '',
                    body: value,
                    footer: '',
                  })}
                  onInsertVariable={insertMergeToken}
                  onTokenBlocked={(msg) => {
                    setLetterTemplateSuccess('');
                    setLetterTemplateError(msg);
                    setTimeout(() => setLetterTemplateError(''), 3000);
                  }}
                  isPreviewMode={isPreviewMode}
                  templateType={editingLetterTemplate.template_type}
                />
              </div>
            )}

            {/* Legacy Letter Template Editor Modal - Keep for reference but hidden */}
            {false && editingLetterTemplate && (
              <div className="modal-overlay" onClick={() => setEditingLetterTemplate(null)}>
                <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Edit {
                      editingLetterTemplate.template_type === 'estimate' ? 'Estimate' :
                      editingLetterTemplate.template_type === 'invoice' ? 'Invoice' :
                      'Letter'
                    } Template</h2>
                    <button className="btn-close" onClick={() => setEditingLetterTemplate(null)}>×</button>
                  </div>
                  <form onSubmit={handleLetterTemplateSubmit} className="template-form">
                    {letterTemplateError && (
                      <div className="error-message">{letterTemplateError}</div>
                    )}
                    {letterTemplateSuccess && (
                      <div className="success-message">{letterTemplateSuccess}</div>
                    )}

                    <div className="form-group">
                      <label>Header</label>
                      <div className="rich-text-editor-wrapper">
                        <div className="rich-text-editor-container" style={{ width: '100%', minHeight: '450px' }}>
                          <ReactQuill
                            ref={quillRefs.header}
                            theme="snow"
                            value={letterTemplateFormData.header || ''}
                            onChange={(value) => handleLetterTemplateQuillChange('header', value)}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Header text (appears at top of letter)"
                            className="letter-template-editor"
                            style={{ width: '100%', display: 'block' }}
                            readOnly={false}
                          />
                        </div>
                        <div className="variable-selector">
                          <h4>Available Variables</h4>
                          <p className="variable-hint">Click to insert into template</p>
                          <div className="variable-buttons">
                            {letterTemplateVariables.map((variable) => (
                              <button
                                key={variable.value}
                                type="button"
                                className="variable-button"
                                onClick={() => insertLetterVariable('header', variable.value)}
                                title={variable.description}
                              >
                                {variable.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Body *</label>
                      <div className="rich-text-editor-wrapper">
                        <div className="rich-text-editor-container" style={{ width: '100%', minHeight: '500px' }}>
                          <ReactQuill
                            ref={quillRefs.body}
                            theme="snow"
                            value={letterTemplateFormData.body || ''}
                            onChange={(value) => handleLetterTemplateQuillChange('body', value)}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Body text (main content of letter)"
                            className="letter-template-editor"
                            style={{ width: '100%', display: 'block' }}
                            readOnly={false}
                          />
                        </div>
                        <div className="variable-selector">
                          <h4>Available Variables</h4>
                          <p className="variable-hint">Click to insert into template</p>
                          <div className="variable-buttons">
                            {letterTemplateVariables.map((variable) => (
                              <button
                                key={variable.value}
                                type="button"
                                className="variable-button"
                                onClick={() => insertLetterVariable('body', variable.value)}
                                title={variable.description}
                              >
                                {variable.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Footer</label>
                      <div className="rich-text-editor-wrapper">
                        <div className="rich-text-editor-container" style={{ width: '100%', minHeight: '450px' }}>
                          <ReactQuill
                            ref={quillRefs.footer}
                            theme="snow"
                            value={letterTemplateFormData.footer || ''}
                            onChange={(value) => handleLetterTemplateQuillChange('footer', value)}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Footer text (appears at bottom of letter)"
                            className="letter-template-editor"
                            style={{ width: '100%', display: 'block' }}
                            readOnly={false}
                          />
                        </div>
                        <div className="variable-selector">
                          <h4>Available Variables</h4>
                          <p className="variable-hint">Click to insert into template</p>
                          <div className="variable-buttons">
                            {letterTemplateVariables.map((variable) => (
                              <button
                                key={variable.value}
                                type="button"
                                className="variable-button"
                                onClick={() => insertLetterVariable('footer', variable.value)}
                                title={variable.description}
                              >
                                {variable.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleResetLetterTemplate}
                      >
                        Reset to Default
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setEditingLetterTemplate(null)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        Save Template
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Application Settings Tab */}
        {activeTab === 'settings' && (
          <div className="app-settings-container">
            {settingsMessage.text && (
              <div className={settingsMessage.type === 'success' ? 'success-message' : 'error-message'} role="alert">
                {settingsMessage.text}
              </div>
            )}

            {/* Display */}
            <section className="app-settings-card">
              <h3 className="app-settings-card-title">Display</h3>
              <p className="app-settings-card-desc">Scale the interface text from 80% to 140%. Applies across the app.</p>
              <div className="app-settings-row">
                <label htmlFor="textSize" className="app-settings-label">Interface text size</label>
                <div className="app-settings-control text-size-wrap">
                  <input
                    type="range"
                    id="textSize"
                    min="0.8"
                    max="1.4"
                    step="0.05"
                    value={textSize}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setTextSize(v);
                      try {
                        localStorage.setItem('appTextSize', String(v));
                        document.documentElement.style.setProperty('--app-text-size', String(v));
                      } catch (_) {}
                    }}
                    className="text-size-slider"
                  />
                  <span className="text-size-value" aria-live="polite">{Math.round(textSize * 100)}%</span>
                </div>
              </div>
            </section>

            {/* Invoicing Defaults */}
            <section className="app-settings-card">
              <h3 className="app-settings-card-title">Invoicing Defaults</h3>
              <p className="app-settings-card-desc">This tax rate will be automatically applied to all new invoices and estimates.</p>
              <form onSubmit={handleSaveTaxRate} className="app-settings-form">
                <div className="app-settings-row">
                  <label htmlFor="defaultTaxRate" className="app-settings-label">Default Tax Rate (%)</label>
                  <div className="app-settings-control tax-rate-wrap">
                    <input
                      type="number"
                      id="defaultTaxRate"
                      min="0"
                      max="100"
                      step="0.01"
                      value={defaultTaxRate}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') setDefaultTaxRate('');
                        else {
                          const n = parseFloat(v, 10);
                          if (!Number.isNaN(n)) setDefaultTaxRate(n);
                        }
                      }}
                      disabled={loadingTaxRate}
                      className="form-input app-settings-input"
                      aria-invalid={defaultTaxRate !== '' && (defaultTaxRate < 0 || defaultTaxRate > 100)}
                    />
                    <span className="input-adornment">%</span>
                  </div>
                </div>
                <div className="app-settings-actions">
                  <button type="submit" className="btn-primary" disabled={loadingTaxRate || savingTaxRate || !taxRateDirty || !taxRateValid}>
                    {savingTaxRate ? 'Saving…' : 'Save Tax Rate'}
                  </button>
                </div>
              </form>
            </section>

            {/* Invoice Behavior */}
            <section className="app-settings-card">
              <h3 className="app-settings-card-title">Invoice Behavior</h3>
              <p className="app-settings-card-desc">Enable or disable the due date on estimates and invoices. When enabled, the due date is shown and can be set; when disabled, it is hidden and not used.</p>
              <form onSubmit={handleSaveEnableDueDates} className="app-settings-form">
                <div className="app-settings-row app-settings-row-checkbox">
                  <label htmlFor="enableDueDates" className="app-settings-label app-settings-label-checkbox">
                    <input
                      type="checkbox"
                      id="enableDueDates"
                      checked={enableDueDates}
                      onChange={(e) => setEnableDueDates(e.target.checked)}
                      className="form-checkbox app-settings-checkbox"
                    />
                    <span>Enable Due Dates</span>
                  </label>
                </div>
                <div className="app-settings-actions">
                  <button type="submit" className="btn-primary" disabled={savingDueDates || !dueDatesDirty}>
                    {savingDueDates ? 'Saving…' : 'Save Due Date Setting'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        {/* Clock In/Out Log Tab (Admin/Manager Only) */}
        {activeTab === 'clock-in-log' && isAdminOrManager(user) && (
          <div className="settings-section">
            <div className="section-header">
              <h2>Clock In/Out Log</h2>
            </div>
            <p className="section-description">
              View all employee clock in/out records, broken down by week.
            </p>

            <div className="clock-in-log-filters">
              <label className="clock-in-log-filter-label">
                <span>Employee</span>
                <select
                  className="clock-in-log-filter-select"
                  value={clockInLogEmployeeFilter}
                  onChange={(e) => setClockInLogEmployeeFilter(e.target.value)}
                  aria-label="Filter by employee"
                >
                  <option value="">All employees</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.raw_user_meta_data?.full_name || u.email || u.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="clock-in-log-filter-label">
                <span>Week</span>
                <select
                  className="clock-in-log-filter-select"
                  value={clockInLogWeekFilter}
                  onChange={(e) => setClockInLogWeekFilter(e.target.value)}
                  aria-label="Filter by week"
                >
                  <option value="all">All time</option>
                  <option value="current">This week</option>
                  <option value="last">Last week</option>
                  <option value="2">2 weeks ago</option>
                  <option value="3">3 weeks ago</option>
                  <option value="4">4 weeks ago</option>
                </select>
              </label>
            </div>

            {loadingClockInLog ? (
              <div className="loading-state">Loading clock in/out log...</div>
            ) : (
              <div className="clock-in-log-list">
                {clockInLog.length === 0 ? (
                  <div className="empty-state">
                    <p>No clock in/out records found.</p>
                  </div>
                ) : (() => {
                  // Group records by week (Monday start)
                  const getWeekKey = (dateStr) => {
                    const d = new Date(dateStr);
                    const day = d.getDay();
                    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
                    const mon = new Date(d);
                    mon.setDate(diff);
                    mon.setHours(0, 0, 0, 0);
                    return mon.getTime();
                  };
                  const byWeek = {};
                  clockInLog.forEach((record) => {
                    const key = getWeekKey(record.clock_in_time);
                    if (!byWeek[key]) byWeek[key] = [];
                    byWeek[key].push(record);
                  });
                  const weekKeys = Object.keys(byWeek).map(Number).sort((a, b) => b - a);

                  return weekKeys.map((weekKey) => {
                    const records = byWeek[weekKey];
                    const weekStart = new Date(weekKey);
                    const weekLabel = `Week of ${weekStart.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`;
                    return (
                      <div key={weekKey} className="clock-in-log-week-section">
                        <h3 className="clock-in-log-week-heading">{weekLabel}</h3>
                        <table className="clock-in-log-table">
                          <thead>
                            <tr>
                              <th>Employee</th>
                              <th>Clock In</th>
                              <th>Clock Out</th>
                              <th>Duration</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {records.map((record) => {
                              const clockInTime = new Date(record.clock_in_time);
                              const clockOutTime = record.clock_out_time ? new Date(record.clock_out_time) : null;
                              const duration = clockOutTime 
                                ? Math.round((clockOutTime - clockInTime) / (1000 * 60))
                                : null;
                              const hours = duration ? Math.floor(duration / 60) : null;
                              const minutes = duration ? duration % 60 : null;
                              const durationText = duration 
                                ? `${hours}h ${minutes}m`
                                : clockOutTime 
                                  ? '0m'
                                  : 'In Progress';
                              const userName = record.users?.raw_user_meta_data?.full_name || record.users?.email || 'Unknown User';
                              return (
                                <tr key={record.id}>
                                  <td>{userName}</td>
                                  <td>{clockInTime.toLocaleString()}</td>
                                  <td>{clockOutTime ? clockOutTime.toLocaleString() : <span className="in-progress">In Progress</span>}</td>
                                  <td>{durationText}</td>
                                  <td>{record.notes || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit-log' && (
          <div className="settings-section">
            <h2>Audit Log</h2>
            <p className="section-description">
              View all actions performed by users in the system.
            </p>

            <div className="audit-log-filters">
              <input
                type="text"
                className="audit-filter-search"
                placeholder="Search action, type, or user..."
                value={auditFilters.search}
                onChange={(e) => setAuditFilters((prev) => ({ ...prev, search: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && loadAuditLog(1)}
                aria-label="Search audit log"
              />
              <input
                type="date"
                className="audit-filter-date"
                value={auditFilters.dateFrom}
                onChange={(e) => setAuditFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                aria-label="From date"
              />
              <input
                type="date"
                className="audit-filter-date"
                value={auditFilters.dateTo}
                onChange={(e) => setAuditFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                aria-label="To date"
              />
              <select
                className="audit-filter-select"
                value={auditFilters.entityType}
                onChange={(e) => setAuditFilters((prev) => ({ ...prev, entityType: e.target.value }))}
                aria-label="Filter by entity type"
              >
                <option value="">All types</option>
                <option value="client">Client</option>
                <option value="company">Company</option>
                <option value="calendar_event">Calendar event</option>
                <option value="invoice">Invoice</option>
                <option value="estimate">Estimate</option>
                <option value="quote">Quote</option>
                <option value="client_note">Client note</option>
              </select>
              <select
                className="audit-filter-select"
                value={auditFilters.action}
                onChange={(e) => setAuditFilters((prev) => ({ ...prev, action: e.target.value }))}
                aria-label="Filter by action"
              >
                <option value="">All actions</option>
                <option value="create">Created</option>
                <option value="update">Updated</option>
                <option value="delete">Deleted</option>
                <option value="mark_paid">Marked as paid</option>
                <option value="finalize">Finalized</option>
                <option value="finalize_draft">Finalized draft</option>
              </select>
              <div className="audit-filter-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => loadAuditLog(1)}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const cleared = { search: '', dateFrom: '', dateTo: '', entityType: '', action: '' };
                    setAuditFilters(cleared);
                    loadAuditLog(1, cleared);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            {loadingAuditLog ? (
              <div className="loading-state">Loading audit log...</div>
            ) : (
              <div className="audit-log-list">
                {auditLog.length === 0 ? (
                  <div className="empty-state">
                    <p>No audit log entries found.</p>
                  </div>
                ) : (
                  <>
                    <div className="audit-log-pagination">
                      <span className="audit-log-count">
                        Showing {auditTotal === 0 ? 0 : (auditPage - 1) * AUDIT_PAGE_SIZE + 1}–{Math.min(auditPage * AUDIT_PAGE_SIZE, auditTotal)} of {auditTotal}
                      </span>
                      <div className="audit-log-pagination-buttons">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={auditPage <= 1}
                          onClick={() => loadAuditLog(auditPage - 1)}
                          aria-label="Previous page"
                        >
                          Previous
                        </button>
                        <span className="audit-log-page-num">Page {auditPage} of {Math.max(1, Math.ceil(auditTotal / AUDIT_PAGE_SIZE))}</span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={auditPage >= Math.ceil(auditTotal / AUDIT_PAGE_SIZE)}
                          onClick={() => loadAuditLog(auditPage + 1)}
                          aria-label="Next page"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                    <div className="audit-container">
                    <table className="audit-log-table">
                      <thead>
                        <tr>
                          <th>Date/Time</th>
                          <th>User</th>
                          <th>Action</th>
                          <th>Entity Type</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                      {auditLog.map((entry) => {
                        let details = {};
                        try {
                          details = entry.details ? JSON.parse(entry.details) : {};
                        } catch {
                          details = {};
                        }
                        
                        return (
                          <tr key={entry.id}>
                            <td>{new Date(entry.created_at).toLocaleString()}</td>
                            <td>{entry.user?.email || 'System'}</td>
                            <td>
                              <span className="action-badge">{formatAuditAction(entry.action, entry.entity_type)}</span>
                            </td>
                            <td>{entry.entity_type}</td>
                            <td>
                              {Object.keys(details).length > 0 ? (
                                <div className="audit-details">
                                  {Object.entries(details).map(([key, value]) => (
                                    <span key={key} className="detail-item">
                                      <strong>{key}:</strong> {String(value)}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showAddUser && (
        <div className="modal-overlay" onClick={() => {
          setShowAddUser(false);
          setEditingUser(null);
          setFormData({ email: '', password: '', full_name: '', role: 'tech' });
          setError('');
          setSuccess('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button 
                className="btn-close" 
                onClick={() => {
                  setShowAddUser(false);
                  setEditingUser(null);
                  setFormData({ email: '', password: '', full_name: '', role: 'tech' });
                  setError('');
                  setSuccess('');
                }}
                aria-label="Close modal"
              >×</button>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              {error && (
                <div className="error-message" role="alert">{error}</div>
              )}
              {success && (
                <div className="success-message" role="status">{success}</div>
              )}

              <div className="form-group">
                <label htmlFor="user-email">Email *</label>
                <input
                  id="user-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  disabled={!!editingUser}
                  style={{ backgroundColor: editingUser ? '#f7fafc' : 'white', cursor: editingUser ? 'not-allowed' : 'text' }}
                  aria-describedby={editingUser ? "email-hint" : undefined}
                />
                {editingUser && (
                  <small id="email-hint" className="form-hint">Email cannot be changed</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="user-password">Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                <input
                  id="user-password"
                  type="password"
                  required={!editingUser}
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? "Leave blank to keep current password" : "Minimum 6 characters"}
                  aria-describedby="password-hint"
                  disabled={editingUser && user?.id !== editingUser.id}
                />
                <small id="password-hint" className="form-hint" style={{ display: 'block', marginTop: '0.25rem' }}>
                  {editingUser 
                    ? (user?.id === editingUser.id 
                        ? 'Leave blank to keep current password' 
                        : 'Password cannot be changed for other users')
                    : 'Must be at least 6 characters'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="user-full-name">Full Name</label>
                <input
                  id="user-full-name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="User's full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-role">Role</label>
                <select
                  id="user-role"
                  value={formData.role === 'user' ? 'tech' : formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="tech">Tech</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddUser(false);
                    setEditingUser(null);
                    setFormData({ email: '', password: '', full_name: '', role: 'tech' });
                    setError('');
                    setSuccess('');
                  }}
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Source Modal */}
      {showAddSource && (
        <div className="modal-overlay" onClick={() => {
          setShowAddSource(false);
          setEditingSource(null);
          setSourceFormData({ name: '' });
          setSourceError('');
          setSourceSuccess('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSource ? 'Edit Source' : 'Add New Source'}</h2>
              <button 
                className="btn-close" 
                onClick={() => {
                  setShowAddSource(false);
                  setEditingSource(null);
                  setSourceFormData({ name: '' });
                  setSourceError('');
                  setSourceSuccess('');
                }}
                aria-label="Close modal"
              >×</button>
            </div>
            <form onSubmit={handleSourceSubmit} className="source-form">
              {sourceError && (
                <div className="error-message" role="alert">{sourceError}</div>
              )}
              {sourceSuccess && (
                <div className="success-message" role="status">{sourceSuccess}</div>
              )}

              <div className="form-group">
                <label htmlFor="source-name">Source Name *</label>
                <input
                  id="source-name"
                  type="text"
                  required
                  value={sourceFormData.name}
                  onChange={(e) => setSourceFormData({ name: e.target.value })}
                  placeholder="e.g., Website, Referral, Walk-in"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddSource(false);
                    setEditingSource(null);
                    setSourceFormData({ name: '' });
                    setSourceError('');
                    setSourceSuccess('');
                  }}
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingSource ? 'Update Source' : 'Create Source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Task Type Modal */}
      {showAddTaskType && (
        <div className="modal-overlay" onClick={() => {
          setShowAddTaskType(false);
          setEditingTaskType(null);
          setTaskTypeError('');
          setTaskTypeSuccess('');
        }}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTaskType ? 'Edit Task Type' : 'Add Task Type'}</h2>
              <button
                className="btn-close"
                onClick={() => {
                  setShowAddTaskType(false);
                  setEditingTaskType(null);
                  setTaskTypeError('');
                  setTaskTypeSuccess('');
                }}
                aria-label="Close modal"
              >×</button>
            </div>
            <form onSubmit={handleTaskTypeSubmit} className="source-form">
              {taskTypeError && (
                <div className="error-message" role="alert">{taskTypeError}</div>
              )}
              {taskTypeSuccess && (
                <div className="success-message" role="status">{taskTypeSuccess}</div>
              )}

              <div className="form-row form-row-two">
                <div className="form-group">
                  <label htmlFor="task-type-name">Name *</label>
                  <input
                    id="task-type-name"
                    type="text"
                    required
                    value={taskTypeFormData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setTaskTypeFormData(prev => ({
                        ...prev,
                        name,
                        code: editingTaskType ? prev.code : (prev.code || name.toLowerCase().replace(/\s+/g, '_')),
                      }));
                    }}
                    placeholder="e.g. Follow-Up"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="task-type-code">Code *</label>
                  <input
                    id="task-type-code"
                    type="text"
                    required
                    value={taskTypeFormData.code}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, code: e.target.value.replace(/\s+/g, '_').toLowerCase() }))}
                    placeholder="e.g. follow_up"
                  />
                  <small className="form-hint">Unique slug used in the system (e.g. in calendar).</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="task-type-description">Description</label>
                <input
                  id="task-type-description"
                  type="text"
                  value={taskTypeFormData.description}
                  onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>

              <div className="form-row form-row-two">
                <div className="form-group">
                  <label htmlFor="task-type-default-status">Default status</label>
                  <select
                    id="task-type-default-status"
                    value={taskTypeFormData.default_status}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, default_status: e.target.value }))}
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="arrived">Arrived</option>
                    <option value="started">Started</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="task-type-display-order">Display order</label>
                  <input
                    id="task-type-display-order"
                    type="number"
                    min={0}
                    value={taskTypeFormData.display_order}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, display_order: parseInt(e.target.value, 10) || 0 }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.active}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, active: e.target.checked }))}
                  />
                  Active (shown in task type dropdown)
                </label>
              </div>

              <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Completion behavior</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                When a user marks a task of this type as completed, you can require a completion modal and choose which options to show.
              </p>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.requires_completion_modal}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, requires_completion_modal: e.target.checked }))}
                  />
                  Show completion modal (user must confirm completion; optional notes and follow-up below apply only when modal is shown)
                </label>
              </div>

              {taskTypeFormData.requires_completion_modal && (
                <div className="completion-actions-group completion-steps-editor">
                  <h3 className="completion-steps-title">Modal steps</h3>
                  <div className="completion-step-card">
                    <div className="form-group completion-step-toggle">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={taskTypeFormData.allow_completion_note}
                          onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, allow_completion_note: e.target.checked }))}
                        />
                        Step 1: Completion notes
                      </label>
                    </div>
                    {taskTypeFormData.allow_completion_note && (
                      <>
                        <div className="form-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={taskTypeFormData.require_completion_note}
                              onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, require_completion_note: e.target.checked }))}
                            />
                            Require completion note (user must enter a note to complete)
                          </label>
                        </div>
                        <div className="form-group">
                          <label>Step label</label>
                          <input
                            type="text"
                            value={taskTypeFormData.completion_note_label}
                            onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, completion_note_label: e.target.value }))}
                            placeholder="Completion Notes"
                          />
                        </div>
                        <div className="form-group">
                          <label>Placeholder text (notes field)</label>
                          <input
                            type="text"
                            value={taskTypeFormData.completion_note_placeholder}
                            onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, completion_note_placeholder: e.target.value }))}
                            placeholder="Add any notes about task completion..."
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="completion-step-card">
                    <div className="form-group completion-step-toggle">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={taskTypeFormData.allow_create_follow_up}
                          onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, allow_create_follow_up: e.target.checked }))}
                        />
                        Step 2: Create follow-up task
                      </label>
                    </div>
                    {taskTypeFormData.allow_create_follow_up && (
                      <>
                        <div className="form-group">
                          <label>Step label</label>
                          <input
                            type="text"
                            value={taskTypeFormData.follow_up_label}
                            onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, follow_up_label: e.target.value }))}
                            placeholder="Create Follow-Up Task"
                          />
                        </div>
                        <div className="form-group">
                          <label>Hint text (below checkbox)</label>
                          <input
                            type="text"
                            value={taskTypeFormData.follow_up_hint}
                            onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, follow_up_hint: e.target.value }))}
                            placeholder="Check this to automatically create a follow-up task..."
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="completion-step-card">
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Reschedule option</h4>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={taskTypeFormData.allow_reschedule_needed}
                          onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, allow_reschedule_needed: e.target.checked }))}
                        />
                        Allow &quot;Reschedule needed&quot; (user can complete with just a note when rescheduling)
                      </label>
                    </div>
                    {taskTypeFormData.allow_reschedule_needed && (
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={taskTypeFormData.require_note_when_reschedule}
                            onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, require_note_when_reschedule: e.target.checked }))}
                          />
                          Require note when reschedule is selected
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="completion-step-card">
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Measurement / order form</h4>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={taskTypeFormData.requires_measurements}
                          onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, requires_measurements: e.target.checked }))}
                        />
                        Requires measurements (order form fields when completing)
                      </label>
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={taskTypeFormData.link_to_invoice}
                          onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, link_to_invoice: e.target.checked }))}
                        />
                        Link to invoice (task can be associated with an invoice)
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Arrival & start time</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                When marking a task as &quot;Arrived&quot; or &quot;Started&quot;, you can require the user to enter the actual time.
              </p>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.require_arrival_time}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, require_arrival_time: e.target.checked }))}
                  />
                  Require arrival time entry (user must enter when they arrived)
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.require_start_time}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, require_start_time: e.target.checked }))}
                  />
                  Require start time entry (user must enter when they started the task)
                </label>
              </div>

              <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Button visibility</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Hide specific buttons in the task types table or on calendar task cards.
              </p>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.hide_edit_button}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, hide_edit_button: e.target.checked }))}
                  />
                  Hide Edit button (in task types table)
                </label>
                <label className="checkbox-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.hide_completion_button}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, hide_completion_button: e.target.checked }))}
                  />
                  Hide Completion button (in task types table)
                </label>
                <label className="checkbox-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.hide_delete_button}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, hide_delete_button: e.target.checked }))}
                  />
                  Hide Delete button (in task types table)
                </label>
                <label className="checkbox-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={taskTypeFormData.hide_complete_button}
                    onChange={(e) => setTaskTypeFormData(prev => ({ ...prev, hide_complete_button: e.target.checked }))}
                  />
                  Hide Complete button (on calendar task cards)
                </label>
              </div>

              <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddTaskType(false);
                    setEditingTaskType(null);
                    setTaskTypeError('');
                    setTaskTypeSuccess('');
                  }}
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTaskType ? 'Update Task Type' : 'Create Task Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Completion Modal (completion-only for a task type) */}
      {editingCompletionModalTaskType && (
        <div className="modal-overlay" onClick={() => setEditingCompletionModalTaskType(null)}>
          <div className="modal-content modal-large completion-modal-editor" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit completion modal – {editingCompletionModalTaskType.name}</h2>
              <button className="btn-close" onClick={() => setEditingCompletionModalTaskType(null)}>×</button>
            </div>
            <form onSubmit={handleCompletionModalUpdate}>
              <div className="modal-body">
                {completionModalSaveError && <div className="error-message">{completionModalSaveError}</div>}
                {completionModalSaveSuccess && <div className="success-message">{completionModalSaveSuccess}</div>}
                <p className="form-hint completion-modal-intro">
                  Configure whether the completion modal is shown and edit the steps (labels and text) users see when completing a task.
                </p>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={completionModalFormData.requires_completion_modal}
                      onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, requires_completion_modal: e.target.checked }))}
                    />
                    Show completion modal (user must confirm completion; steps below apply only when modal is shown)
                  </label>
                </div>
                {completionModalFormData.requires_completion_modal && (
                  <div className="completion-steps-editor">
                    <h3 className="completion-steps-title">Modal steps</h3>
                    <div className="completion-step-card">
                      <div className="form-group completion-step-toggle">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={completionModalFormData.allow_completion_note}
                            onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, allow_completion_note: e.target.checked }))}
                          />
                          Step 1: Completion notes
                        </label>
                      </div>
                      {completionModalFormData.allow_completion_note && (
                        <>
                          <div className="form-group">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={completionModalFormData.require_completion_note}
                                onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, require_completion_note: e.target.checked }))}
                              />
                              Require completion note
                            </label>
                          </div>
                          <div className="form-group">
                            <label>Step label</label>
                            <input
                              type="text"
                              value={completionModalFormData.completion_note_label}
                              onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, completion_note_label: e.target.value }))}
                              placeholder="Completion Notes"
                            />
                          </div>
                          <div className="form-group">
                            <label>Placeholder text (notes field)</label>
                            <input
                              type="text"
                              value={completionModalFormData.completion_note_placeholder}
                              onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, completion_note_placeholder: e.target.value }))}
                              placeholder="Add any notes about task completion..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="completion-step-card">
                      <div className="form-group completion-step-toggle">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={completionModalFormData.allow_create_follow_up}
                            onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, allow_create_follow_up: e.target.checked }))}
                          />
                          Step 2: Create follow-up task
                        </label>
                      </div>
                      {completionModalFormData.allow_create_follow_up && (
                        <>
                          <div className="form-group">
                            <label>Step label</label>
                            <input
                              type="text"
                              value={completionModalFormData.follow_up_label}
                              onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, follow_up_label: e.target.value }))}
                              placeholder="Create Follow-Up Task"
                            />
                          </div>
                          <div className="form-group">
                            <label>Hint text (below checkbox)</label>
                            <input
                              type="text"
                              value={completionModalFormData.follow_up_hint}
                              onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, follow_up_hint: e.target.value }))}
                              placeholder="Check this to automatically create a follow-up task..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="completion-step-card">
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Reschedule option</h4>
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={completionModalFormData.allow_reschedule_needed}
                            onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, allow_reschedule_needed: e.target.checked }))}
                          />
                          Allow &quot;Reschedule needed&quot;
                        </label>
                      </div>
                      {completionModalFormData.allow_reschedule_needed && (
                        <div className="form-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={completionModalFormData.require_note_when_reschedule}
                              onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, require_note_when_reschedule: e.target.checked }))}
                            />
                            Require note when reschedule is selected
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="completion-step-card">
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Measurement / order form</h4>
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={completionModalFormData.requires_measurements}
                            onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, requires_measurements: e.target.checked }))}
                          />
                          Requires measurements (order form fields)
                        </label>
                      </div>
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={completionModalFormData.link_to_invoice}
                            onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, link_to_invoice: e.target.checked }))}
                          />
                          Link to invoice
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <hr className="completion-modal-hr" style={{ margin: '1.25rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                <h3 className="completion-steps-title">Arrival & start time</h3>
                <p className="form-hint" style={{ marginBottom: '0.75rem' }}>
                  When marking a task as &quot;Arrived&quot; or &quot;Started&quot;, require the user to enter the actual time.
                </p>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={completionModalFormData.require_arrival_time}
                      onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, require_arrival_time: e.target.checked }))}
                    />
                    Require arrival time entry
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={completionModalFormData.require_start_time}
                      onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, require_start_time: e.target.checked }))}
                    />
                    Require start time entry
                  </label>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <h3 className="completion-steps-title">Button visibility</h3>
                <p className="form-hint" style={{ marginBottom: '0.75rem' }}>
                  Hide specific buttons in the task types table or on calendar task cards.
                </p>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={completionModalFormData.hide_edit_button}
                    onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, hide_edit_button: e.target.checked }))}
                  />
                  Hide Edit button (in task types table)
                </label>
                <label className="checkbox-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={completionModalFormData.hide_completion_button}
                    onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, hide_completion_button: e.target.checked }))}
                  />
                  Hide Completion button (in task types table)
                </label>
                <label className="checkbox-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={completionModalFormData.hide_delete_button}
                    onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, hide_delete_button: e.target.checked }))}
                  />
                  Hide Delete button (in task types table)
                </label>
                <label className="checkbox-label" style={{ display: 'block', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={completionModalFormData.hide_complete_button}
                    onChange={(e) => setCompletionModalFormData(prev => ({ ...prev, hide_complete_button: e.target.checked }))}
                  />
                  Hide Complete button (on calendar task cards)
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setEditingCompletionModalTaskType(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update completion modal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Email Template Modal */}
      {editingTemplate && (
        <div className="modal-overlay" onClick={() => setEditingTemplate(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit {
                editingTemplate.template_type === 'estimate' ? 'Estimate' :
                editingTemplate.template_type === 'unpaid_invoice' ? 'Unpaid Invoice' :
                'Paid Invoice/Receipt'
              } Email Template</h2>
              <button className="btn-close" onClick={() => setEditingTemplate(null)}>×</button>
            </div>
            <form onSubmit={handleTemplateSubmit} className="template-form">
              {templateError && (
                <div className="error-message">{templateError}</div>
              )}
              {templateSuccess && (
                <div className="success-message">{templateSuccess}</div>
              )}

              <div className="form-group">
                <label>Email Subject *</label>
                <input
                  type="text"
                  name="subject"
                  required
                  value={templateFormData.subject}
                  onChange={handleTemplateFormChange}
                  placeholder="Subject of the email"
                />
                <div className="variable-buttons">
                  {availableVariables.filter(v => v.value !== '{{due_date}}' && v.value !== '{{due_date_section}}').map(v => (
                    <button
                      key={v.value}
                      type="button"
                      className="btn-variable"
                      onClick={() => insertVariable('subject', v.value)}
                      title={v.description}
                    >
                      {v.value}
                    </button>
                  ))}
                </div>
                <small className="form-hint">Click variables above to insert them, or type them manually.</small>
              </div>

              <div className="form-group">
                <label>Email Body *</label>
                <div className="textarea-with-variables">
                  <textarea
                    name="body"
                    rows="15"
                    required
                    value={templateFormData.body}
                    onChange={handleTemplateFormChange}
                    placeholder="Body of the email"
                    className="monospace-textarea"
                  />
                  <div className="variable-selector">
                    <h3>Available Variables:</h3>
                    <div className="variable-list">
                      {availableVariables.map(v => (
                        <button
                          key={v.value}
                          type="button"
                          className="btn-variable-full"
                          onClick={() => insertVariable('body', v.value)}
                          title={v.description}
                        >
                          <code>{v.value}</code>
                          <span>{v.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleResetTemplate}
                >
                  Reset to Default
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingTemplate(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
